import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import DsButton from '../DsButton';

type WebPdfDocumentPreviewProps = {
  blob: Blob;
  fileName: string;
  onError?: (_message: string) => void;
  toolbarActions?: React.ReactNode;
};

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.5;
const ZOOM_STEP = 0.25;
const MAX_OUTPUT_SCALE = 2;
const FIT_GUTTER = 32;

const clampZoom = (value: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));

const loadPdfDocument = async (data: ArrayBuffer): Promise<PDFDocumentProxy> => {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
  return pdfjsLib.getDocument({ data }).promise;
};

export default function WebPdfDocumentPreview({
  blob,
  fileName,
  onError,
  toolbarActions,
}: WebPdfDocumentPreviewProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const pagesRef = useRef<HTMLDivElement | null>(null);
  const renderSequenceRef = useRef(0);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);
  const [rendering, setRendering] = useState(true);

  useEffect(() => {
    let active = true;
    let loadedDocument: PDFDocumentProxy | null = null;
    setPdfDocument(null);
    setPageCount(0);
    setZoom(1);
    setRendering(true);

    blob.arrayBuffer()
      .then(loadPdfDocument)
      .then((document) => {
        loadedDocument = document;
        if (!active) {
          document.destroy();
          return;
        }
        setPdfDocument(document);
        setPageCount(document.numPages);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setRendering(false);
        onError?.(error instanceof Error ? error.message : 'PDFを表示できませんでした。');
      });

    return () => {
      active = false;
      renderSequenceRef.current += 1;
      loadedDocument?.destroy();
    };
  }, [blob, onError]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    const updateSize = () => {
      const nextSize = {
        width: Math.round(viewport.clientWidth),
        height: Math.round(viewport.clientHeight),
      };
      setContainerSize((currentSize) => (
        currentSize.width === nextSize.width && currentSize.height === nextSize.height
          ? currentSize
          : nextSize
      ));
    };
    updateSize();
    if (typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(updateSize);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  const renderPages = useCallback(async () => {
    const pagesElement = pagesRef.current;
    if (!pdfDocument || !pagesElement || containerSize.width <= 0 || containerSize.height <= 0) return;
    const sequence = renderSequenceRef.current + 1;
    renderSequenceRef.current = sequence;
    setRendering(true);
    pagesElement.replaceChildren();

    try {
      const availableWidth = Math.max(containerSize.width - FIT_GUTTER, 1);
      const availableHeight = Math.max(containerSize.height - FIT_GUTTER, 1);
      for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
        if (renderSequenceRef.current !== sequence) return;
        const page = await pdfDocument.getPage(pageNumber);
        const naturalViewport = page.getViewport({ scale: 1 });
        const fitScale = Math.min(
          availableWidth / naturalViewport.width,
          availableHeight / naturalViewport.height,
        );
        const viewport = page.getViewport({ scale: fitScale * zoom });
        const outputScale = Math.min(Math.max(window.devicePixelRatio || 1, 1), MAX_OUTPUT_SCALE);
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { alpha: false });
        if (!context) throw new Error('PDF描画領域を初期化できませんでした。');
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        canvas.setAttribute('aria-label', `${fileName} ${pageNumber}ページ目`);

        const pageElement = document.createElement('div');
        pageElement.className = 'pdf-document-preview__page';
        pageElement.appendChild(canvas);
        pagesElement.appendChild(pageElement);

        await page.render({
          canvasContext: context,
          viewport,
          transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
        }).promise;
      }
    } catch (error: unknown) {
      if (renderSequenceRef.current === sequence) {
        onError?.(error instanceof Error ? error.message : 'PDFを表示できませんでした。');
      }
    } finally {
      if (renderSequenceRef.current === sequence) setRendering(false);
    }
  }, [containerSize.height, containerSize.width, fileName, onError, pdfDocument, zoom]);

  useEffect(() => {
    renderPages();
  }, [renderPages]);

  return (
    <div className="pdf-document-preview">
      <div className="pdf-document-preview__toolbar" role="toolbar" aria-label="PDF表示操作">
        <div className="pdf-document-preview__identity">
          <span className="pdf-document-preview__filename" title={fileName}>{fileName}</span>
          <span className="pdf-document-preview__pages" aria-label={`全${pageCount}ページ`}>
            {pageCount ? `1 / ${pageCount}` : '- / -'}
          </span>
        </div>
        <div className="pdf-document-preview__controls">
          <div className="pdf-document-preview__zoom" aria-label="表示倍率">
            <DsButton
              variant="secondary"
              size="sm"
              icon
              aria-label="縮小"
              title="縮小"
              disabled={zoom <= MIN_ZOOM}
              onClick={() => setZoom((value) => clampZoom(value - ZOOM_STEP))}
            >
              <i className="fa-solid fa-minus" aria-hidden="true" />
            </DsButton>
            <span>{Math.round(zoom * 100)}%</span>
            <DsButton
              variant="secondary"
              size="sm"
              icon
              aria-label="拡大"
              title="拡大"
              disabled={zoom >= MAX_ZOOM}
              onClick={() => setZoom((value) => clampZoom(value + ZOOM_STEP))}
            >
              <i className="fa-solid fa-plus" aria-hidden="true" />
            </DsButton>
          </div>
          {toolbarActions ? (
            <div className="pdf-document-preview__actions">{toolbarActions}</div>
          ) : null}
        </div>
      </div>
      <div ref={viewportRef} className="pdf-document-preview__viewport">
        {rendering ? <div className="pdf-document-preview__loading" role="status">PDFを描画しています...</div> : null}
        <div ref={pagesRef} className="pdf-document-preview__pages-list" />
      </div>
    </div>
  );
}
